<template>
  <div>
    <ActionableInsight
      :tabs="tabs"
      :documents="documents"
      :statements="statements"

      table-label="Non-Compliant Live Jobs"
      :disable-share="isSharedPage"
      :is-shared-page="isSharedPage"
    >
      <template #noResultsFoundText>
        <h5>Nice Work!</h5>
        <p class="compliance-no-result-content">
          It looks like your open job listings are compliant with regulations!
        </p>
      </template>
      <template #legend>
        <ActionableInsightLegend
          :description="legendDescription"
          :items="legendItems"
        />
      </template>
    </ActionableInsight>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia';
import { useInsights } from '@/stores/insights/insights';
import { useActionableInsights } from '@/stores/insights/actionableInsights';
import ActionableInsight from '@/components/insights/ActionableInsight/ActionableInsight.vue';
import ActionableInsightLegend from '@/components/insights/ActionableInsight/ActionableInsightLegend.vue';
import { useAppBase } from '@/stores/app/base';
import { useFilters } from '@/stores/filters/filters';
import { useSubMenu } from '@/stores/filters/subMenu';
import { useDatePicker } from '@/stores/filters/datePicker';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import { useAppAuthData } from '@/stores/app/authData';
import { useInsightsExport } from '@/stores/insights/export';
import { useInsightsShare } from '@/stores/insights/share';

export default {
  name: 'ComplianceReport',
  components: {
    ActionableInsight,
    ActionableInsightLegend,
  },
  emits: ['track-page-load'],
  data() {
    return {
      legendDescription: `Compliance is based on where a job is located and its attributes. 
      For salary compliance, we are focused on jobs which are located in US states that require salary disclosure. 
      For Diversity Statement and Accommodation Statement, these depend on job attributes including whether there are physical labor requirements.`,
      isSharedPage: false,
    };
  },
  computed: {
    ...mapState(useActionableInsights, ['documents', 'totals', 'exclusions']),
    ...mapState(useFilters, ['searchTerm', 'mergedTempFilters', 'myJobsEnabled']),
    ...mapState(useSubMenu, ['selectedSubMenuOption']),
    ...mapState(useAppAuthData, ['authData']),
    ...mapState(useDatePicker, ['dateRange']),
    legendItems() {
      return [
        {
          mood: 'happy',
          description: this.exclusions?.canUseRegulatoryGuidance
            ? 'indicates that the job includes salary or diversity / accommodation statements (in jobs where these are required), has no language issues or has no regulatory guidance issues'
            : 'indicates that the job includes salary, diversity / accommodation statements or benefits (in jobs where these are required) or has no language issues',
        },
        {
          mood: 'sad',
          description: this.exclusions?.canUseRegulatoryGuidance
            ? 'indicates that the job does not include salary or diversity / accommodation statements (in jobs where these are required), has language issues or has regulatory guidance issues'
            : 'indicates that the job does not include salary, diversity / accommodation statements or benefits (in jobs where these are required) or has language issues',
        },
        {
          mood: 'neutral',
          description: this.exclusions?.canUseRegulatoryGuidance
            ? 'indicates that the job does not require salary or diversity / accommodation statements'
            : 'indicates that the job does not require salary, diversity / accommodation statements or benefits',
        },
      ];
    },
    statements() {
      const statements = [
        {
          text: 'Salary',
          align: 'center',
          value: 'issues.salary',
          class: 'missing-statement-header',
        },
        {
          text: 'Statements',
          align: 'center',
          value: 'issues.diversityAccomodation',
          class: 'missing-statement-header',
        },
        {
          text: 'Language',
          align: 'center',
          value: 'issues.exclusionaryLanguage',
          class: 'missing-statement-header',
        },
      ];

      if (this.exclusions?.canUseRegulatoryGuidance) {
        statements.push({
          text: 'Regulations',
          align: 'center',
          value: 'issues.regulatoryGuidance',
          class: 'missing-statement-header',
        });
      } else {
        statements.push({
          text: 'Benefits',
          align: 'center',
          value: 'issues.benefits',
          class: 'missing-statement-header',
        });
      }

      return statements;
    },
    tabLabels() {
      const tabLabels = [
        {
          label: 'Salary Disclosure',
          key: 'percentageMissingSalary',
          description: 'of eligible and live jobs are missing salary.',
          tooltipKey: 'compliance-salary-disclosure',
          exclusionKey: 'salary',
          exclusionDescription: 'Your organization\'s Applicant Tracking System adds salary ranges outside of the JobNav system, and so we cannot measure compliance.',
        },
        {
          label: 'Non-Discrimination Statements',
          key: 'percentageMissingDiversityAccomodation',
          description: 'of eligible and live jobs are missing diversity and/or accommodation statements.',
          tooltipKey: 'compliance-diversity-accommodation',
          exclusionKey: 'diversityAccomodation',
          exclusionDescription: 'Your organization\'s Applicant Tracking System adds diversity and accommodation statements outside of the JobNav system, and so we cannot measure compliance.',
        },
        {
          label: 'Exclusionary Language',
          key: 'percentageMissingExclusionaryLanguage',
          description: 'of your live jobs include exclusionary language.',
          tooltipKey: 'compliance-exclusionary-language',
        },
      ];

      if (this.exclusions?.canUseRegulatoryGuidance) {
        tabLabels.push({
          label: 'Regulatory Guidance',
          key: 'percentageMissingRegulatoryGuidance',
          description: 'of your live jobs are using language that is not compliant with regulatory requirements.',
          tooltipKey: 'compliance-regulatory',
        });
      } else {
        tabLabels.push({
          label: 'Perks & Benefits',
          key: 'percentageMissingBenefits',
          description: 'of your eligible and live jobs are missing perks and benefits.',
          tooltipKey: 'compliance-missing-perks-benefits',
          exclusionKey: 'benefits',
          exclusionDescription: 'Your organization\'s Applicant Tracking System adds benefits outside of the JobNav system, and so we cannot measure compliance.',
        });
      }

      return tabLabels;
    },
    tabs() {
      return this.tabLabels.map((tab) => {
        const value = this.totals[tab.key];

        if (value === undefined) {
          return {
            ...tab,
            description: 'No data available',
            value: 0,
            disabled: true,
          };
        }

        return {
          ...tab,
          description: tab.description,
          // Will show as <1% when percentage is less than 1% and greater than 0%
          value: value < 1 && value > 0 ? '<1' : Math.round(value),
        };
      });
    },
  },
  async mounted() {
    subscribeToFilterUpdates(this.loadPage);

    this.$emit('track-page-load', { page: 'Compliance Report' });

    this.setFilterConfig({
      module: 'insights',
      datepicker: true,
      narrow: true,
      showMyJobs: !window?.sessionStorage?.reportingShareKey,
      subMenuType: 'chip',
    });

    const isSharedPage = window.sessionStorage.reportingShareKey && !this.authData.isLoggedIn;

    this.isSharedPage = Boolean(isSharedPage);

    if (isSharedPage) {
      this.showFilterBarSettings({ route: this.$route.name, options: ['hideDatePicker'] });
      this.resetExportConfig();
      this.resetShareConfig();
    } else {
      this.showFilterBarSettings({ route: this.$route.name, options: ['hideDatePicker'] });

      this.setExportConfig({
        csvExport: true,
        pngExport: false,
        pngWidgetOptions: {},
        csvOptions: [
          {
            label: 'Data Table (.csv)',
            csvDownloadFunction: () => {
              this.downloadComplianceReport({
                filters: this.mergedTempFilters,
                dateRange: this.dateRange,
                myJobsEnabled: this.myJobsEnabled,
                document,
                sort: this.$route.query?.sort || this.selectedSubMenuOption,
              });
            },
            waitTime: 5000,
          },
        ],
      });

      this.setShareConfig({
        shareable: true,
      });
    }

    await this.loadPage({
      routeQuery: this.$route.query || '',
      page: this.$route.query?.page || 1,
      router: this.$router,
      sort: this.$route.query.sort,
      firstLoad: true,
      selectedMyJobs: this.$route.query?.owned === 'true',
      searchTerm: this.searchTerm,
      selectedSubMenuOption: this.selectedSubMenuOption,
    });
  },
  beforeUnmount() {
    unsubscribeToFilterUpdates();

    // Reset table reports data
    this.setActionableInsightsData();

    // Clean up filter config and selected sub menu option
    this.setFilterConfig({
      subMenuType: null,
    });

    this.resetExportConfig();
    this.resetShareConfig();

    this.setSelectedSubMenuOption(null);
  },
  methods: {
    ...mapActions(useAppBase, ['showFilterBarSettings']),
    ...mapActions(useInsights, ['loadPage']),
    ...mapActions(useFilters, ['setFilterConfig']),
    ...mapActions(useSubMenu, ['setSelectedSubMenuOption']),
    ...mapActions(useActionableInsights, ['setActionableInsightsData', 'downloadComplianceReport']),
    ...mapActions(useInsightsExport, ['setExportConfig', 'resetExportConfig']),
    ...mapActions(useInsightsShare, ['resetShareConfig', 'setShareConfig']),

  },
};
</script>

<style lang="less" scoped>
.compliance-no-result-content {
  max-width: 261px;
}
</style>
